import { useCallback, useState } from "react";
import { apiPost } from "../../../api/fetch";
import { endpoints } from "../../../utils/Constants";
import { useReservationStore } from "../../../stores/useReservationStore";
import { useReservationResources } from "./useReservationResources";
import { formatProductMinimumQuantityMessage } from "./finalizeErrorMessages";

export const FinalizeErrorCode = {
  // 10xxx - ticket person field validation (per-field errors in perFieldErrors)
  TicketPersonValidationError: 10000,
  // Per-email ticket cap for the play reached (EVP-13151)
  EmailTicketLimitReached: 10011,
  // 20xxx - request/contact
  EmptyRequest: 20000,
  MissingContactInfo: 20001,
  // 30xxx - ticket holder
  MissingTicketHolderInformation: 30000,
  InvalidTicketHolderInformation: 30001,
  // 40xxx - products
  ProductNotFound: 40000,
  ProductOptionNotFound: 40001,
  ProductQuantityInvalid: 40002,
  ProductShippingNotProvided: 40003,
  ProductShippingNotAvailable: 40004,
  ProductCannotBeShipped: 40005,
  UnableToAddProductToTransaction: 40006,
  ProductMinimumQuantityNotMet: 40007,
  // 50xxx - donation
  InvalidDonationAmount: 50001,
  // split order
  IncludesRenewedTickets: 406,
  IncludesCancelledTickets: 408,
  // Olympiacos access control
  OlympiacosAccessDenied: 70001,
} as const;

export const isOlympiacosAccessError = (code: number | undefined) =>
  code === FinalizeErrorCode.OlympiacosAccessDenied;

// Per-field reason codes from errorCode 10000
const TicketPersonReasonCode = {
  MissingRequired: 1,
  InvalidFormat: 2,
  DuplicateCard: 3,
  AlreadyExists: 4,
  InvalidCard: 5,
  CertificateMissingMembership: 6,
  // EVP-11038: explicit AMKA uniqueness violation (in-batch or persisted).
  AmkaAlreadyUsed: 7,
  // EVP-11021: kids-ticket discount requires holder AMKA of a minor (<18).
  AmkaNotMinorForKidsTicket: 8,
  DuplicateCertificate: 9,
  DuplicateEmails: 10,
  // EVP-13119: certificate number uniqueness (persisted / within the same order).
  CertificateAlreadyUsed: 11,
  CertificateDuplicateInOrder: 12,

  //Olympiacos-specific
  MissingFinancialSupportCoupon: 101,
  DuplicateFinancialSupportCoupon: 102,
  FinancialSupportCouponNotFound: 103,
  FinancialSupportCouponAlreadyUsed: 104,
  MissingMemberCard: 105,
  DuplicateMemberCard: 106,
  MemberCardNotFound: 107,
  MemberCardAlreadyUsed: 108,

  // athletic-events
  MissingFancard: 109,
  FancardNotFound: 110,
  FancardAlreadyUsed: 111,
  DuplicateFancard: 112,

  // KOK-specific
  KOKMissingFancard: 201,
  KOKMissingIdentity: 202,
  KOKMissingGameId: 203,
  KOKInvalidFancard: 204,
  KOKInvalidNationality: 205,
  KOKInvalidIdentity: 206,
  KOKInvalidAge: 207,
  KOKFancardAlreadyUsed: 208,
  KOKFancardBlacklisted: 209,
  KOKFancardExpired: 210,
  KOKEventExpired: 211,
  KOKEventDoesntExist: 212,
  KOKUseOtherMethod: 213,

  // Borrowed fancard - a card belonging to a producer other than the play's own
  IssuedFancardNotFound: 401,
  DuplicateIssuedFancard: 402,
  IssuedFancardAlreadyUsed: 403,
} as const;

// ticketId → attributeId → error message
export type PerFieldErrors = Record<number, Record<string, string>>;
export type FinalizeFieldType =
    | "loyaltycard"
    | "certificationnumber"
    | "sportscard"
    | "email"
    | "placeofresidence"
    | "number"
    | "";

export interface FinalizeError {
  message: string;
  errorCode?: number;
  errorTickets?: number[];
  perFieldErrors?: PerFieldErrors;
  fieldType?: FinalizeFieldType;
  /** EmailTicketLimitReached only: tickets the email can still buy (0 = limit used up). */
  emailLimitRemaining?: number;
}

type Resources = ReturnType<typeof useReservationResources>;

function getErrorMessage(errorCode: number, r: Resources): string {
  return r.PaymentError_Generic.replace("{0}", String(errorCode));
}

function getTicketPersonReasonMessage(reasonCode: number, r: Resources, isKokEvent: boolean, metadata: Object): string {
  switch (reasonCode) {
    case TicketPersonReasonCode.MissingRequired:       return r.FieldRequired;
    case TicketPersonReasonCode.InvalidFormat:         return r.CardInvalid;
    case TicketPersonReasonCode.DuplicateCard:         return r.DuplicateLoyaltyCard;
    case TicketPersonReasonCode.AlreadyExists:         return r.AlreadyPurchasedWithFancard;
    case TicketPersonReasonCode.InvalidCard:           return r.CardInvalid;
    case TicketPersonReasonCode.CertificateMissingMembership:
      const url = window.location.origin + metadata["url"];
      return r.CertificateWithNoMembershipErrorMessage.replace("{0}", url).replace("{1}", url);
    case TicketPersonReasonCode.AmkaAlreadyUsed:       return r.AmkaAlreadyUsed;
    case TicketPersonReasonCode.AmkaNotMinorForKidsTicket: return r.AmkaNotMinorForKidsTicket;
    case TicketPersonReasonCode.DuplicateCertificate:  return r.DuplicateCertificate;
    case TicketPersonReasonCode.DuplicateEmails:  return r.DuplicateEmails;
    case TicketPersonReasonCode.CertificateAlreadyUsed:        return r.CertificateAlreadyUsed;
    case TicketPersonReasonCode.CertificateDuplicateInOrder:   return r.CertificateDuplicateInOrder;

    case TicketPersonReasonCode.MissingFinancialSupportCoupon:      return r.FieldRequired;
    case TicketPersonReasonCode.FinancialSupportCouponNotFound:     return r.CodeInvalid;
    case TicketPersonReasonCode.DuplicateFinancialSupportCoupon:    return r.VoucherOneTicketOnly;
    case TicketPersonReasonCode.FinancialSupportCouponAlreadyUsed:  return r.CodeAlreadyUsed;

    case TicketPersonReasonCode.MissingMemberCard:     return r.FieldRequired;
    case TicketPersonReasonCode.MemberCardNotFound:    return r.CardInvalid;
    case TicketPersonReasonCode.MemberCardAlreadyUsed: return r.CardExceededLimit;
    case TicketPersonReasonCode.DuplicateMemberCard:   return r.DuplicateLoyaltyCard;

    case TicketPersonReasonCode.MissingFancard:        return r.MissingSportscardError;
    case TicketPersonReasonCode.FancardNotFound:       return r.CardInvalid;
    case TicketPersonReasonCode.FancardAlreadyUsed:    return r.CardExceededLimit;
    case TicketPersonReasonCode.DuplicateFancard:      return r.CardOneTicketPerEvent;

    case TicketPersonReasonCode.KOKInvalidFancard:     return r.KOKInvalidSportsCard;
    case TicketPersonReasonCode.KOKFancardAlreadyUsed: return r.KOKportsCardAlreadyUsed;
    case TicketPersonReasonCode.KOKInvalidNationality: return r.InvalidNationality;
    case TicketPersonReasonCode.KOKMissingFancard:     return r.MissingSportscardError;
    case TicketPersonReasonCode.KOKMissingIdentity:    return r.MissingIdentity;
    case TicketPersonReasonCode.KOKMissingGameId:      return r.MissingGameIdError;
    case TicketPersonReasonCode.KOKInvalidIdentity:    return r.InvalidIdentityError;
    case TicketPersonReasonCode.KOKInvalidAge:         return r.InvalidAgeError;
    case TicketPersonReasonCode.KOKFancardBlacklisted: return r.FancardBlacklisted;
    case TicketPersonReasonCode.KOKFancardExpired:     return r.FancardExpired;
    case TicketPersonReasonCode.KOKEventExpired:       return r.MatchHasExpired;
    case TicketPersonReasonCode.KOKEventDoesntExist:   return r.EventDoesntExist;
    case TicketPersonReasonCode.KOKUseOtherMethod:     return r.UnknownCsoError;

    case TicketPersonReasonCode.IssuedFancardNotFound:      return r.CardInvalid;
    case TicketPersonReasonCode.DuplicateIssuedFancard:     return r.CardOneUsePerMatch;
    case TicketPersonReasonCode.IssuedFancardAlreadyUsed:   return r.CardAlreadyUsed;

    default: return r.PaymentError_Generic.replace("{0}", String(reasonCode));
  }
}

function parsePerFieldErrors(
  metadata: Record<string, any> | undefined,
  r: Resources,
  isKokEvent: boolean,
): PerFieldErrors {
  const result: PerFieldErrors = {};
  const ticketsData = metadata?.Tickets?.Ticket;
  if (!ticketsData) return result;

  const ticketArray = Array.isArray(ticketsData) ? ticketsData : [ticketsData];

  for (const ticket of ticketArray) {
    const ticketId = parseInt(ticket["@id"]);
    if (isNaN(ticketId)) continue;

    const fields = ticket.Field;
    if (!fields) continue;

    const fieldArray = Array.isArray(fields) ? fields : [fields];
    result[ticketId] = {};

    for (const field of fieldArray) {
      const attributeId: string | undefined = field["@attributeid"];
      if (!attributeId) continue;
      const reasonCode = parseInt(field["@reason"]);
      const fieldErrorMetadata: Object = field["Metadata"];
      result[ticketId][attributeId] = getTicketPersonReasonMessage(
        isNaN(reasonCode) ? 0 : reasonCode,
        r,
        isKokEvent,
        fieldErrorMetadata
      );
    }
  }

  return result;
}

interface SubmitReservationResult {
  submitReservation: (payload: object) => Promise<FinalizeError | null>;
  isSubmitting: boolean;
  error: FinalizeError | null;
  clearError: () => void;
}

const MOCK_ERROR_RESPONSE = null;

// const MOCK_ERROR_RESPONSE = { errorCode: 10000, response: { errorTickets: [201743295], metadata: { Tickets: { Ticket: { "@id": "201743295", Field: [{ "@attributeid": "abc-123", "@reason": "2" }] } } } } };
// const MOCK_ERROR_RESPONSE = { "message": "Invalid quantity for products", "errorCode": 40002}; // products: quantity invalid
const MOCK_500_ERROR = false; // generic server error → triggers generic error modal
// const MOCK_SUCCESS_RESPONSE = {
//   paymentUrl:
//     "https://dev-travel.viva.gr/cmstemplates/vivatravelaspx/data/payment/pay.ashx?g=6b5376cb-2d18-40c4-ad25-58abf9e5fa73&dt=178ea2cc-434d-4706-a3bd-d4c6e88f7929&caller=https://dev.more.com/reservation/c10cbcde-bc7a-4773-ab90-c4cd5371f2dd&language=el&method=Smart",
//   errorTickets: null,
//   metadata: {},
// };
const MOCK_SUCCESS_RESPONSE = null;

export function useSubmitReservation(isKokEvent = false): SubmitReservationResult {
  const r = useReservationResources();
  const reservationId = useReservationStore((s) => s.reservationId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<FinalizeError | null>(null);

  const submitReservation = useCallback(
    async (payload: object): Promise<FinalizeError | null> => {
      setIsSubmitting(true);
      setError(null);
      let redirecting = false;
      try {
        if (MOCK_500_ERROR) {
          await new Promise((resolve) => setTimeout(resolve, 500));
          throw { message: "Internal Server Error", status: 500 };
        }

        if (MOCK_ERROR_RESPONSE) {
          await new Promise((resolve) => setTimeout(resolve, 500));
          throw { data: { data: MOCK_ERROR_RESPONSE } };
        }

        if (MOCK_SUCCESS_RESPONSE) {
          await new Promise((resolve) => setTimeout(resolve, 500));
          if (MOCK_SUCCESS_RESPONSE.paymentUrl) {
            redirecting = true;
            window.location.href = MOCK_SUCCESS_RESPONSE.paymentUrl;
          }
          return null;
        }

        const result = await apiPost(
          endpoints.reservation.finalize(reservationId),
          payload,
        );
        const paymentUrl = result?.data?.paymentUrl;
        if (paymentUrl) {
          redirecting = true;
          window.location.href = paymentUrl;
        } else {
          throw {};
        }
        return null;
      } catch (err: any) {
        const response = err?.data?.data ?? err?.data;
        const errorCode: number | undefined = response?.errorCode;
        const errorTickets: number[] | undefined =
          response?.response?.errorTickets;
        const metadata: Record<string, any> | undefined =
          response?.response?.metadata;

        let finalizeError: FinalizeError;
        if (errorCode === FinalizeErrorCode.TicketPersonValidationError) {
          const perFieldErrors = parsePerFieldErrors(metadata, r, isKokEvent);
          const message = response?.message ?? r.SomethingWrong;
          finalizeError = { message, errorCode, errorTickets, perFieldErrors };
        } else if (errorCode === FinalizeErrorCode.ProductMinimumQuantityNotMet) {
          const message = formatProductMinimumQuantityMessage(
            response?.message,
            r.ProductMinimumQuantityNotMet,
            {
              single: r.ProductMinimumQuantityNotMetPerTicketSingle,
              plural: r.ProductMinimumQuantityNotMetPerTicket,
              and: r.And,
            },
            metadata,
          );
          finalizeError = { message, errorCode, errorTickets };
        } else if (errorCode === FinalizeErrorCode.EmailTicketLimitReached) {
          // Message stays server-driven; `remaining` only picks the modal title.
          // The BE's <metadata> element arrives wrapped once more: metadata.metadata.emailLimit.
          const emailLimit = metadata?.metadata?.emailLimit ?? metadata?.emailLimit;
          const message = response?.message ?? getErrorMessage(errorCode, r);
          const emailLimitRemaining = Number(emailLimit?.["@remaining"]) || 0;
          finalizeError = { message, errorCode, errorTickets, emailLimitRemaining };
        } else if (errorCode) {
          const message = response?.message ?? getErrorMessage(errorCode, r);
          finalizeError = { message, errorCode, errorTickets };
        } else {
          const message =
            (typeof response === "string" ? response : response?.message) ??
            err?.message ??
            r.PaymentError_Generic.replace("{0}", "");
          finalizeError = { message };
        }
        setError(finalizeError);
        return finalizeError;
      } finally {
        if (!redirecting) setIsSubmitting(false);
      }
    },
    [reservationId, r, isKokEvent],
  );

  const clearError = useCallback(() => setError(null), []);

  return { submitReservation, isSubmitting, error, clearError };
}